<script lang="ts">
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';
	import { DashReoderContainer, DashReorderItem } from '@Shelly/shelly-design-system/index';
	import { devices } from 'src/stores/lists/devices';
import { groups } from 'src/stores/lists/groups';
import { rooms } from 'src/stores/lists/rooms';
import { alarms, temp_schemes } from 'src/stores/lists/scenes-derived';
	import { scenes } from 'src/stores/lists/scenes';
	import { load_image_from } from 'src/helpers/interface_helpers';
	import type { DashWidgetEditType } from '../../dash-types';
	import { generateThermostatIcon } from 'src/components/Lists/list-helpers';
	import { MAX_CARDS_IN_WIDGET } from '../../dash-helpers';
	import { getCameraSelectionLimit } from '../../dash-camera-helpers';
	import { format } from 'svelte-i18n';

	export let editItem: DashWidgetEditType;

	const itemTypesStoreMapper = {
		devices: $devices,
		rooms: $rooms,
		groups: $groups,
		alarms: $alarms,
		scenes: $scenes,
		temp_schemes: $temp_schemes,
	};

	const mapTitleByType = {
		dashAlarm: $format('tab_alarms', { default: 'Alarms' }),
		dashControlDev: $format('tab_devices', { default: 'Devices' }),
		dashParameterDev: $format('tab_devices', { default: 'Devices' }),
		dashQuickDev: $format('tab_devices', { default: 'Devices' }),
		dashCamera: $format('tab_devices', { default: 'Devices' }),
		dashScene: $format('tab_scenes', { default: 'Scenes' }),
		dashGroup: $format('tab_groups', { default: 'Groups' }),
		dashRoom: $format('tab_rooms', { default: 'Rooms' }),
		dashMedia: $format('tab_devices', { default: 'Devices' }),
		dashThermostat: $format('tab_thermostats', { default: 'Тhermostats' }),
		dashLight: $format('tab_scenes', { default: 'Scenes' }),
	};

	const MISSING_ITEM_ICON = 'fa-light fa-triangle-exclamation text-color-warning';

	const mapMissingItemText = {
		devices: $format('label_missing_device', {
			default: 'Missing Device',
		}),
		rooms: $format('label_missing_room', {
			default: 'Missing Room',
		}),
		groups: $format('label_missing_group', {
			default: 'Missing Group',
		}),
		alarms: $format('label_missing_alarm', {
			default: 'Missing Alarm',
		}),
		scenes: $format('label_missing_scene', {
			default: 'Missing Scene',
		}),
		temp_schemes: $format('label_missing_thermostat', {
			default: 'Missing Thermostat',
		}),
	};

	$: items = [
		...editItem.selectItems.map((item) => {
			const curentItem = itemTypesStoreMapper?.[item.itemType]?.[item?.id];
			const image = curentItem?.image || curentItem?._meta?.image;
			const icon =
				item.itemType === 'temp_schemes'
					? generateThermostatIcon(curentItem, 'fat')
					: curentItem?.icon || curentItem?._meta?.icon;

			return {
				...item,
				isDndShadowItem: false,
				title: curentItem?.name || curentItem?._meta.name || mapMissingItemText[item.itemType],
				avatar: {
					image: image && load_image_from(image),
					icon: curentItem ? icon : MISSING_ITEM_ICON,
					backgroundColor: curentItem?.backgroundColor || curentItem?._meta?.backgroundColor,
				},
			};
		}),
	];
	let dragDisabled = false;

	const flipDurationMs = 0;

	let instructions =
		'Press space to activate reordering. Use arrow keys to move the item. Press space again to confirm.';
	let announceMove = '';

	const handleStartDrag = () => {
		if (items.length <= 1) {
			dragDisabled = true;
		} else {
			dragDisabled = false;
		}
	};

	const handleEndDrag = () => {
		dragDisabled = true;
	};

	const handleReorder = (e: CustomEvent) => {
		items = e.detail.items;
	};

	const handleReorderEnd = async (e: CustomEvent) => {
		items = [...e.detail.items];
		editItem.selectItems = items.map((item) => {
			delete item?.isDndShadowItem;
			delete item?.title;
			delete item?.avatar;
			return {
				...item,
			};
		});
		handleEndDrag();
	};
</script>

<DashReoderContainer
	title={`${mapTitleByType[editItem?.widgetType]} (${editItem.selectItems.length}/${editItem?.widgetType === 'dashCamera' ? getCameraSelectionLimit(editItem.cameraConfig?.variant) : MAX_CARDS_IN_WIDGET})`}
	id="dnd-widget-reorder"
	aria-label="Drag and drop wrapper"
	a11yProps={{
		dndInstructions: instructions,
		dndAnnounceMsg: announceMove,
	}}
	dndSettings={{
		items: items,
		dragDisabled: dragDisabled,
		flipDurationMs: flipDurationMs,
		type: 'dashWizardReorder',
	}}
	on:consider={handleReorder}
	on:finalize={handleReorderEnd}
>
	{#each items as item, index (item.id)}
		<div
			class={classMap({
				'dash-list-item': true,
				'dnd-placeholder dash': item.isDndShadowItem,
			})}
			on:focus={() => {
				if (dragDisabled) {
					dragDisabled = false;
				}
			}}
		>
			<DashReorderItem
				class={classMap({ 'cursor-not-allowed': items.length <= 1 })}
				title={item.title}
				avatar={item.avatar}
				on:handle-drag-start={handleStartDrag}
				on:handle-drag-end={handleEndDrag}
			/>
		</div>
	{/each}
</DashReoderContainer>
